import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { PostCard } from './types'
import { usePublish } from './hooks/usePublish'
import PublishFilterDrawer from './components/publish/PublishFilterDrawer'
import { resolvePortraitCover } from './lib/cover'
import { shouldRenderMicroCard } from './lib/feedCards'
import { resolveMediaUrl } from './lib/mediaUrl'
import { DEFAULT_MICRO_TEMPLATE_ID, resolveMicroTemplateBackground } from './features/editor/microTemplates'
import type { PublishFilters } from './types/publish'
import { DEFAULT_PUBLISH_FILTERS } from './types/publish'
import { PixeCard } from './components/ui/PixeCard'
import { paidBadgeLabel, type AccessLevel } from './features/editor/access'
import MobileActionBar from './components/pixe/MobileActionBar'
import { useGiftFlow } from './hooks/useGiftFlow'
import GiftDrawer from './components/gifts/GiftDrawer'
import GiftToast from './components/gifts/GiftToast'
import GiftCelebration from './components/gifts/GiftCelebration'
import CommentsDrawer from './components/comments/CommentsDrawer'
import MicroMetaFooter, { formatRelativeTime, formatViews } from './components/micro/MicroMetaFooter'
import { recordPostView } from './api/posts'
import FormatWatermark from './components/common/FormatWatermark'

import NavBar from './components/layout/NavBar'

// NavBar moved to components/layout/NavBar
export const TOP_FEED_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'trending', label: 'Trending' },
  { key: 'newest', label: 'Newest' },
  { key: 'popular', label: 'Popular' },
  { key: 'local', label: 'Local' },
] as const

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(99,102,241,0.25),transparent_70%)]" />
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
          Content. Community. Commerce.
        </h1>
        <p className="mt-4 text-lg text-white/80 max-w-2xl">
          Create, discover, and shop from the people you follow. A
          beautiful, functional space to read, write, and purchase.
        </p>
        <div className="mt-8 flex gap-3">
          <a href="#feed" className="inline-flex items-center rounded-xl bg-brand px-5 py-3 font-semibold shadow hover:bg-brand-600">Explore Feed</a>
          <a href="#store" className="inline-flex items-center rounded-xl border border-white/15 px-5 py-3 font-semibold hover:bg-white/5">Browse Store</a>
        </div>
      </div>
    </section>
  )
}

function RailSection({ title, children, right }: { title: string; children: any; right?: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-extrabold tracking-tight">{title}</h2>
        {right ?? (
          <a className="text-sm text-white/80 hover:text-white" href="#">See all</a>
        )}
      </div>
      {children}
    </section>
  )
}

function Card({ item }: { item: PostCard | undefined }) {
  if (!item) return <div className="glass rounded-xl h-48" />
  const cover = resolvePortraitCover(item)

  const title = item?.title || item?.user?.displayName || 'Untitled'
  const href = item?.format === 'job' && item?.job?.id ? `/jobs/${item.job.id}` : `/p/${item?.id}`

  return (
    <Link to={href} className="glass rounded-xl overflow-hidden block hover:shadow-lg hover:shadow-black/20">
      <div className="aspect-[16/9] bg-white/5">
        {cover ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img src={cover} alt={`${title} cover image`} className="h-full w-full object-cover" />
        ) : null}
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold">{title}</div>
        {!!item?.subtitle && <div className="text-xs text-white/70">{item.subtitle}</div>}
      </div>
    </Link>
  )
}

export default function App() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qParam = searchParams.get('q') || ''
  const modeParam = searchParams.get('mode') || ''
  const [activeChip, setActiveChip] = useState(
    'trending' as 'all' | 'trending' | 'newest' | 'popular' | 'local'
  )
  const feedSort = activeChip === 'newest' ? 'newest' : 'popular'
  const [filters, setFilters] = useState<PublishFilters>({ ...DEFAULT_PUBLISH_FILTERS, sort: feedSort })
  const [localHint, setLocalHint] = useState<string | null>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  useEffect(() => {
    setFilters((prev) => ({ ...prev, sort: feedSort }))
  }, [feedSort])
  useEffect(() => {
    setFilters((prev) => ({ ...prev, q: qParam }))
  }, [qParam])
  useEffect(() => {
    if (activeChip !== 'local') {
      setLocalHint(null)
      setFilters((prev) => ({
        ...prev,
        is_local: false,
        location_mode: prev.location_mode === 'near_me' ? 'anywhere' : prev.location_mode,
        near_lat: prev.location_mode === 'near_me' ? null : prev.near_lat,
        near_lng: prev.location_mode === 'near_me' ? null : prev.near_lng,
      }))
      return
    }
    setFilters((prev) => ({ ...prev, is_local: true }))
    if (filters.near_lat == null && filters.near_lng == null && filters.location_mode !== 'city') {
      setLocalHint('Set location in Filters to refine.')
    } else {
      setLocalHint(null)
    }
  }, [activeChip, filters.location_mode, filters.near_lat, filters.near_lng])
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      mode:
        modeParam === 'saved'
          ? 'saved'
          : modeParam === 'my_subscriptions'
          ? 'my_subscriptions'
          : modeParam === 'subscriptions'
          ? 'subscriptions'
          : 'all',
    }))
  }, [modeParam])
  const { items: feed, error, hasMore, loading, loadMore } = usePublish(filters)
  const [people, setPeople] = useState<any[] | null>(null)
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [storeItems, setStoreItems] = useState<any[] | null>(null)
  const [storeError, setStoreError] = useState<string | null>(null)

  // other effects moved into hooks for People/Store routes

  const cards = useMemo(() => (feed && feed.length ? feed : Array.from({ length: 8 }).map((_, i) => ({ id: i } as any))), [feed])
  const filteredCards = useMemo(() => cards, [cards])

  const giftFlow = useGiftFlow()
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentsPostId, setCommentsPostId] = useState<number | null>(null)
  const [viewOverrides, setViewOverrides] = useState<Record<number, number>>({})
  const viewTimersRef = useRef<Map<number, number>>(new Map())

  // Mobile (phone) fullscreen feed state: determine which item is "active" for ActionRail.
  const mobileFeedRef = useRef<HTMLDivElement | null>(null)
  const [activeMobileIndex, setActiveMobileIndex] = useState(0)
  const tapRef = useRef<{ id: number; x: number; y: number; moved: boolean; t: number } | null>(null)
  useEffect(() => {
    const root = mobileFeedRef.current
    if (!root) return
    if (!feed || filteredCards.length === 0) return

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-feed-item="1"]'))
    if (els.length === 0) return

    const obs = new IntersectionObserver(
      (entries) => {
        const best = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio - a.intersectionRatio))[0]
        if (!best) return
        const idx = Number((best.target as HTMLElement).dataset.index || '0')
        if (Number.isFinite(idx)) setActiveMobileIndex(idx)
      },
      { root, threshold: [0.55, 0.7, 0.85] }
    )
    els.forEach((el) => obs.observe(el))
    return () => obs.disconnect()
  }, [feed, filteredCards.length])

  // Count a view only after real watch time in mobile feed:
  // >= 60% visible for >= 1s, deduped per post for 30 minutes.
  useEffect(() => {
    const root = mobileFeedRef.current
    if (!root || !feed || filteredCards.length === 0) return

    const seenWindowMs = 30 * 60 * 1000
    const keyFor = (id: number) => `micro:viewed:${id}`
    const isDeduped = (id: number) => {
      try {
        const raw = sessionStorage.getItem(keyFor(id))
        if (!raw) return false
        const ts = Number(raw)
        return Number.isFinite(ts) && Date.now() - ts < seenWindowMs
      } catch {
        return false
      }
    }
    const markSeen = (id: number) => {
      try { sessionStorage.setItem(keyFor(id), String(Date.now())) } catch {}
    }

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-feed-item="1"]'))
    if (els.length === 0) return

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = Number((entry.target as HTMLElement).dataset.index || '-1')
          const post = Number.isFinite(idx) ? (filteredCards[idx] as PostCard | undefined) : undefined
          const postId = Number(post?.id)
          if (!post || !Number.isFinite(postId) || postId <= 0 || post.format !== 'micro') continue

          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            if (viewTimersRef.current.has(postId)) continue
            const timer = window.setTimeout(async () => {
              viewTimersRef.current.delete(postId)
              if (isDeduped(postId)) return
              markSeen(postId)
              try {
                const res = await recordPostView(postId)
                const next = Number((res as any)?.views_count)
                setViewOverrides((prev) => ({
                  ...prev,
                  [postId]: Number.isFinite(next)
                    ? next
                    : Math.max(Number(prev[postId] ?? post?.views_count ?? 0), 0) + 1,
                }))
              } catch {
                // best effort
              }
            }, 1000)
            viewTimersRef.current.set(postId, timer)
          } else {
            const t = viewTimersRef.current.get(postId)
            if (t != null) {
              window.clearTimeout(t)
              viewTimersRef.current.delete(postId)
            }
          }
        }
      },
      { root, threshold: [0.6] }
    )

    els.forEach((el) => obs.observe(el))
    return () => {
      obs.disconnect()
      for (const t of viewTimersRef.current.values()) window.clearTimeout(t)
      viewTimersRef.current.clear()
    }
  }, [feed, filteredCards])

  // Mobile infinite-ish loading: when close to the end, fetch more.
  useEffect(() => {
    if (!feed) return
    if (!hasMore || loading) return
    const remaining = filteredCards.length - 1 - activeMobileIndex
    if (remaining <= 2) loadMore()
  }, [activeMobileIndex, feed, filteredCards.length, hasMore, loadMore, loading])

  // Restore mobile feed scroll after returning from reader.
  useEffect(() => {
    const root = mobileFeedRef.current
    if (!root) return
    if (!feed) return
    try {
      const raw = sessionStorage.getItem('mobileFeed:scrollTop')
      if (!raw) return
      const top = Number(raw)
      if (!Number.isFinite(top)) return
      root.scrollTop = top
      sessionStorage.removeItem('mobileFeed:scrollTop')
    } catch {}
  }, [feed])

  // Top creators (public)
  useEffect(() => {
    let cancelled = false
    fetch('/api/people/featured/')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((arr) => {
        if (!cancelled) setPeople(Array.isArray(arr) ? arr.slice(0, 12) : [])
      })
      .catch((e) => !cancelled && setPeopleError(e?.message || 'Failed to load people'))
    return () => {
      cancelled = true
    }
  }, [])

  // Featured listings (auth required -> show message if 401)
  useEffect(() => {
    let cancelled = false
    fetch('/api/pixe_store/store/listings/')
      .then(async (r) => {
        if (r.status === 401) throw new Error('Sign in required to view store listings')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => !cancelled && setStoreItems(Array.isArray(data?.results) ? data.results.slice(0, 8) : []))
      .catch((e) => !cancelled && setStoreError(e?.message || 'Failed to load listings'))
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="min-h-full">
      <NavBar mobileFixed showMobileMenuButton={false} />

      {/* Phone mode (< md): TikTok-style fullscreen feed */}
      <div className="md:hidden fixed inset-0 bg-black">
        {/* Compact overlay controls (kept out of the snap list) */}
        <div
          className="fixed left-0 right-0 z-30 px-3 pt-2"
          style={{
            top: 'var(--nav-h, 56px)',
            paddingTop: 'calc(0.5rem + env(safe-area-inset-top))',
          }}
        >
          <div className="mx-auto max-w-lg">
            <div className="flex items-center justify-between gap-2">
              <div />
              <button
                type="button"
                aria-label="Filters"
                onClick={() => setFilterOpen(true)}
                className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-black/30 backdrop-blur hover:bg-black/40"
                title="Filter Pixes"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/90" aria-hidden="true">
                  <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div
          ref={mobileFeedRef}
          className="h-[100dvh] w-full overflow-y-auto snap-y snap-mandatory overscroll-contain no-scrollbar"
        >
          {filteredCards.map((c: any, i: number) => {
            if (!feed) {
              return (
                <div key={i} data-feed-item="1" data-index={i} className="snap-start h-[100dvh] w-full relative bg-neutral-950">
                  <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_20%,rgba(99,102,241,0.18),transparent_70%)]" />
                  <div className="absolute left-0 right-0 bottom-0 p-6">
                    <div className="glass rounded-2xl h-24" />
                  </div>
                </div>
              )
            }
            const img = resolvePortraitCover(c)
            const lvl = (c?.access_level || 'FREE') as AccessLevel
            const badge = paidBadgeLabel(lvl, c?.required_tier_rank ?? null)
            const viewsCount = Number(viewOverrides[c.id] ?? c?.views_count ?? 0)
            const hasProof = Boolean(c?.has_proof || c?.receipt_id || c?.chain_receipt_id)
            const isMicro = shouldRenderMicroCard(c)
            const microText = String(c?.micro_text || c?.title || '').trim()
            const microBg = resolveMicroTemplateBackground(c?.micro_template_id || DEFAULT_MICRO_TEMPLATE_ID)
            return (
              <div
                key={c.id ?? i}
                data-feed-item="1"
                data-index={i}
                className="snap-start h-[100dvh] w-full relative bg-black"
                onPointerDown={(e) => {
                  // Only treat taps that start on non-interactive surfaces.
                  const t = e.target as HTMLElement
                  if (t?.closest?.('button,a,input,textarea,select,label,[role="button"],[data-no-feed-tap="1"]')) return
                  tapRef.current = { id: (e as any).pointerId ?? 0, x: e.clientX, y: e.clientY, moved: false, t: Date.now() }
                }}
                onPointerMove={(e) => {
                  const s = tapRef.current
                  if (!s) return
                  const dx = Math.abs(e.clientX - s.x)
                  const dy = Math.abs(e.clientY - s.y)
                  if (dx > 12 || dy > 12) s.moved = true
                }}
                onPointerUp={(e) => {
                  const s = tapRef.current
                  tapRef.current = null
                  if (!s) return
                  const dt = Date.now() - s.t
                  if (s.moved || dt > 650) return
                  const t = e.target as HTMLElement
                  if (t?.closest?.('button,a,input,textarea,select,label,[role="button"],[data-no-feed-tap="1"]')) return
                  try {
                    if (mobileFeedRef.current) sessionStorage.setItem('mobileFeed:scrollTop', String(mobileFeedRef.current.scrollTop))
                  } catch {}
                  const href = c?.format === 'job' && c?.job?.id ? `/jobs/${c.job.id}` : `/p/${c.id}`
                  navigate(href, { state: { fromMobileFeed: true } as any })
                }}
              >
                {isMicro ? (
                  <div className="absolute inset-0" style={{ background: microBg }} />
                ) : img ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={img} alt={`${c.title || c.user?.displayName || 'Pixe'} cover image`} className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-white/5" />
                )}

                {/* Scrims for text readability */}
                <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/55 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/85 to-transparent" />

                {/* Metadata overlay */}
                <div className="absolute left-0 right-0 bottom-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                  {isMicro ? (
                    <div className="max-w-[82%]">
                      <FormatWatermark format={c?.format} category={c?.category} />
                      <div className="mt-3 whitespace-pre-line text-[clamp(1.75rem,5vw,2.85rem)] font-semibold leading-[1.12] text-white drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]">
                        {microText || 'Your micro text preview will appear here.'}
                      </div>
                      <div className="mt-4 flex items-center gap-2">
                        {c?.user?.avatar ? (
                          // eslint-disable-next-line jsx-a11y/img-redundant-alt
                          <img src={resolveMediaUrl(c.user.avatar) || c.user.avatar} alt={`${c?.user?.displayName || 'Creator'} avatar`} className="h-9 w-9 rounded-full border border-white/20 object-cover" />
                        ) : (
                          <div className="h-9 w-9 rounded-full border border-white/20 bg-white/15" />
                        )}
                        <div className="min-w-0 max-w-[58%]">
                          <div className="text-[15px] font-semibold leading-tight text-white/95 truncate">{c?.user?.displayName || 'Creator'}</div>
                          <div className="text-[11px] leading-tight text-white/70 truncate">{c?.user?.handle || ''}</div>
                        </div>
                      </div>
                      <div className="mt-2 text-right">
                        <MicroMetaFooter
                          createdAt={c?.created_at || null}
                          viewsCount={viewsCount}
                          hasProof={hasProof}
                          receiptStatus={c?.receipt_status || null}
                          compact
                          className="shrink-0 whitespace-nowrap"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-[75%]">
                      <FormatWatermark format={c?.format} category={c?.category} className="mb-2" />
                      {badge ? (
                        <div className="mb-2 inline-flex items-center rounded-full bg-black/45 border border-white/10 px-2 py-1 text-[11px] text-white/85 backdrop-blur">
                          {badge}
                        </div>
                      ) : null}
                      <div className="text-xl font-extrabold leading-tight drop-shadow">{c.title || c.user?.displayName || 'Untitled'}</div>
                      <div className="mt-1 text-sm text-white/85 drop-shadow">
                        {c.user?.displayName ? c.user.displayName : 'Creator'}{c.user?.handle ? ` \u2022 @${String(c.user.handle).replace(/^@/, '')}` : ''}
                      </div>
                      {c.subtitle ? <div className="mt-2 text-sm text-white/80 max-h-10 overflow-hidden drop-shadow">{c.subtitle}</div> : null}
                      <div className="mt-2">
                        <MicroMetaFooter
                          createdAt={c?.created_at || null}
                          viewsCount={viewsCount}
                          hasProof={hasProof}
                          receiptStatus={c?.receipt_status || null}
                          compact
                          className="shrink-0 whitespace-nowrap"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <MobileActionBar
                  postId={c.id}
                  counts={c.counts}
                  myState={c.my_state}
                  user={c.user}
                  shareUrl={c.share_url}
                  onOpenComments={() => {
                    setCommentsPostId(c.id)
                    setCommentsOpen(true)
                  }}
                  onGift={() => {
                    if (!feed) return
                    giftFlow.openForPost(c as any)
                  }}
                />
              </div>
            )
          })}
        </div>

      </div>

      {/* Gift drawer + toast (mobile feed) */}
      {giftFlow.post ? (
        <>
          <GiftDrawer
            open={giftFlow.isOpen}
            onClose={giftFlow.close}
            creatorName={giftFlow.post?.user?.displayName}
            avatarUrl={resolveMediaUrl(giftFlow.post?.user?.avatar) || undefined}
            catalog={giftFlow.catalogDefs}
            onSend={({ gift }) => giftFlow.onSend({ gift })}
          />
          <GiftToast
            visible={giftFlow.toast.visible}
            emoji={giftFlow.toast.emoji}
            label={giftFlow.toast.label}
            detail={giftFlow.toast.detail}
            kind={giftFlow.toast.kind}
            onHide={giftFlow.toast.hide}
          />
          <GiftCelebration visible={giftFlow.toast.visible && giftFlow.toast.kind === 'success' && giftFlow.toast.celebrate} />
        </>
      ) : null}

      {/* Comments drawer (mobile feed) */}
      {commentsPostId != null ? (
        <CommentsDrawer
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          target={{ type: 'post', id: commentsPostId }}
        />
      ) : null}

      {/* Desktop + tablet (>= md): keep existing layout unchanged */}
      <main className="hidden md:block">
        {/* Chips row (consistent with People/Store) */}
        <div className="mx-auto max-w-6xl px-4 py-3">
          <div className="hscroll no-scrollbar flex gap-2 overflow-x-auto whitespace-nowrap">
            {TOP_FEED_CHIPS.map((chip) => (
              <button
                key={chip.key}
                onClick={() => setActiveChip(chip.key as any)}
                className={`px-2.5 py-1 text-xs md:text-sm rounded-full border ${
                  activeChip === chip.key ? 'bg-brand border-transparent' : 'bg-white/5 border-white/10'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
          {localHint ? <div className="mt-2 text-xs text-white/65">{localHint}</div> : null}
        </div>

        <RailSection
          title="Trending"
          right={
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                aria-label="Filters"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                title="Filter Pixes"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                  <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z"/>
                </svg>
              </button>
              <Link
                to="/publish"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs md:text-sm font-semibold text-white shadow hover:bg-brand-600"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                </svg>
                Publish
              </Link>
            </div>
          }
        >
          {error ? (
            <div className="text-sm text-red-400">{error}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredCards.map((c: any, i: number) => {
                  if (!feed) return <div key={i} className="glass rounded-xl h-48" />
                  const img = resolvePortraitCover(c)
                  const isMicro = shouldRenderMicroCard(c)
                  const microBg = resolveMicroTemplateBackground(c?.micro_template_id || DEFAULT_MICRO_TEMPLATE_ID)
                  const microText = String(c?.micro_text || c?.title || '').trim()
                  const lvl = (c?.access_level || 'FREE') as AccessLevel
                  const badge = paidBadgeLabel(lvl, c?.required_tier_rank ?? null)
                  const viewsCount = Number(viewOverrides[c.id] ?? c?.views_count ?? 0)
                  const hasProof = Boolean(c?.has_proof || c?.receipt_id || c?.chain_receipt_id)
                  const meta =
                    c?.format === 'micro'
                      ? `${formatRelativeTime(c?.created_at || null)} \u2022 ${formatViews(viewsCount)} views${hasProof ? ` \u2022 ${c?.receipt_status === 'pending' ? 'Proof pending' : c?.receipt_status === 'failed' ? 'Proof failed' : 'Proof'}` : ''}`
                      : null
                  if (isMicro) {
                    return (
                      <Link key={c.id ?? i} to={`/p/${c.id}`} className="group relative rounded-xl overflow-hidden block hover:shadow-lg hover:shadow-black/30 transition-transform duration-200 ease-out will-change-transform hover:scale-[1.015]">
                        <div className="aspect-[8/9] relative">
                          <div className="absolute inset-0" style={{ background: microBg }} />
                          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/55 to-transparent" />
                          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/90 to-transparent" />
                          <FormatWatermark format={c?.format} category={c?.category} className="absolute left-3 top-3 text-[10px]" />
                          <div className="absolute left-3 right-3 bottom-16 whitespace-pre-line text-xl font-semibold leading-[1.14] text-white line-clamp-6">
                            {microText || 'Your micro text preview will appear here.'}
                          </div>
                          <div className="absolute left-3 right-3 bottom-3 flex items-end justify-between gap-2">
                            <div className="min-w-0 max-w-[56%]">
                              <div className="text-sm font-semibold text-white/95 truncate">{c?.user?.displayName || 'Creator'}</div>
                              <div className="text-[11px] text-white/70 truncate">{c?.user?.handle || ''}</div>
                            </div>
                            <MicroMetaFooter
                              createdAt={c?.created_at || null}
                              viewsCount={viewsCount}
                              hasProof={hasProof}
                              receiptStatus={c?.receipt_status || null}
                              compact
                              className="shrink-0 whitespace-nowrap"
                            />
                          </div>
                        </div>
                      </Link>
                    )
                  }
                  const isJob = c?.format === 'job' && c?.job
                  const jobTitle = isJob ? (c.job.title || c.title || 'Job') : (c.title || c.user?.displayName)
                  const jobSubtitle = isJob
                    ? [c.job.job_kind, c.job.location_mode, c.job.summary].filter(Boolean).join(' • ')
                    : c.subtitle
                  const href = c?.format === 'job' && c?.job?.id ? `/jobs/${c.job.id}` : `/p/${c.id}`
                  return (
                    <PixeCard
                      key={c.id ?? i}
                      title={jobTitle}
                      subtitle={jobSubtitle}
                      meta={meta}
                      image={img}
                      badge={badge}
                      href={href}
                      watermarkFormat={c?.format}
                      watermarkCategory={c?.category}
                    />
                  )
                })}
              </div>
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10 disabled:opacity-60"
                  >
                    {loading ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </RailSection>

        <RailSection
          title="Top Creators"
          right={<Link className="text-sm text-white/80 hover:text-white" to="/people">See all</Link>}
        >
          {peopleError && <div className="text-sm text-red-400">{peopleError}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {!people && !peopleError && Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="glass rounded-xl p-3 h-24" />
            ))}
            {people?.map((p, i) => {
              const avatarSrc = resolveMediaUrl(p.avatar)
              return (
              <Link
                key={p.public_id ?? i}
                to={p?.public_id ? `/people/i/${encodeURIComponent(p.public_id)}` : '/people'}
                className="glass rounded-xl p-3 text-center hover:bg-white/5"
              >
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    className="mx-auto mb-2 h-12 w-12 rounded-full object-cover"
                    onError={(e) => {
                      const img = e.currentTarget
                      img.style.display = 'none'
                      const fallback = img.nextElementSibling as HTMLElement | null
                      if (fallback) fallback.style.display = 'block'
                    }}
                  />
                ) : null}
                <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-white/10" style={{ display: avatarSrc ? 'none' : 'block' }} />
                <div className="text-sm font-semibold truncate">{p.displayName}</div>
                <div className="text-xs text-white/70 truncate">{p.handle}</div>
              </Link>
              )
            })}
          </div>
        </RailSection>

        <RailSection
          title="Featured Listings"
          right={<Link className="text-sm text-white/80 hover:text-white" to="/store">See all</Link>}
        >
          {storeError && <div className="text-sm text-white/80">{storeError}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {!storeItems && !storeError && Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="glass rounded-xl h-48" />
            ))}
            {storeItems?.map((it, i) => {
              const img = it.images?.[0]?.public_url || it.product?.thumbnail_media?.public_url
              const title = it.product?.title || 'Listing'
              return (
                <PixeCard key={it.public_id ?? i} title={title} image={img || null} href={it.share_url || '#'} />
              )
            })}
          </div>
        </RailSection>
      </main>
      {/* Filters drawer controlling the Trending grid */}
      <PublishFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onApply={(next) => {
          if (next.location_mode === 'near_me' && (next.near_lat == null || next.near_lng == null)) setLocalHint('Set location in Filters to refine.')
          else setLocalHint(null)
          setFilters(next)
          setFilterOpen(false)
        }}
        onClear={() => setFilters({ ...DEFAULT_PUBLISH_FILTERS, sort: feedSort })}
      />
      <footer className="mt-12 border-t border-white/10">
        <div className="mx-auto max-w-6xl px-4 py-8 text-sm text-white/60">© {new Date().getFullYear()} PixeLoop</div>
      </footer>
    </div>
  )
}




